import { Select, Space } from 'antd'
import React, { memo, useCallback, useEffect, useMemo } from 'react'
import type { Models } from '@wolf/api'
import { useUrlGlobalFilterParams } from '../useUrlGlobalFilterParams'
import { trpc } from '../utils/trpc'

type Props = {
  sourceFilter: Models.SourceFilterType
  setSourceFilter: React.Dispatch<React.SetStateAction<Models.SourceFilterType>>
}

export const SourcePicker = memo(({ sourceFilter, setSourceFilter }: Props) => {
  const { changeSourceParams, sourceFilterParams } = useUrlGlobalFilterParams()

  const sources = trpc.sources.getSources.useQuery(undefined, {
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5 // 5 minutes
  }).data

  // Optimize effect to run only when necessary values change, not on every location change
  useEffect(() => {
    if (sourceFilter.serialId === -1) return

    // Only set URL params if they don't exist yet
    if (!sourceFilterParams.serialId || !sourceFilterParams.name) {
      changeSourceParams({
        serialId: sourceFilter.serialId,
        replace: true,
      })
      return
    }

    // Only update local state if URL params differ from current state
    if (sourceFilterParams.serialId !== sourceFilter.serialId) {
      setSourceFilter({
        serialId: sourceFilterParams.serialId,
        name: sourceFilterParams.name,
        language: sourceFilterParams.language,
        isRetail: sourceFilterParams.isRetail,
      })
    }
  }, [sourceFilter.serialId, sourceFilterParams.serialId, sourceFilterParams.name, sourceFilterParams.language, changeSourceParams, setSourceFilter])

  // Memoize the options to prevent recreating on every render
  const selectOptions = useMemo(() => {
    return sources?.map((source) => ({
      label: source.name,
      value: source.serialId,
    })) || []
  }, [sources])

  // Memoize the onChange handler
  const handleSourceChange = useCallback((value: number) => {
    const source = sources?.find((source) => source.serialId === value)

    const newSourceFilter = {
      serialId: value,
      name: source?.name || 'WETACA',
      language: source?.language || 'ES',
      isRetail: source?.isRetail ?? undefined,
    }

    setSourceFilter(newSourceFilter)

    changeSourceParams({
      serialId: Number(value),
      replace: false
    })
  }, [sources, setSourceFilter, changeSourceParams])

  return (
    <>
      Source:{' '}
      <Space>
        <Select
          value={sourceFilter.serialId}
          size="small"
          onChange={handleSourceChange}
          style={{ minWidth: '10rem' }}
          showSearch
          options={selectOptions}
        />
      </Space>
    </>
  )
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  return (
    prevProps.sourceFilter.serialId === nextProps.sourceFilter.serialId &&
    prevProps.sourceFilter.name === nextProps.sourceFilter.name &&
    prevProps.sourceFilter.language === nextProps.sourceFilter.language &&
    prevProps.sourceFilter.isRetail === nextProps.sourceFilter.isRetail
  )
})
